import { useEffect } from 'react';

/**
 * Sets <meta name="robots" content="noindex, nofollow"> on the document head
 * for the lifetime of the calling component, then removes it on unmount.
 *
 * Use on pages or layouts that must not appear in search engines.
 *
 * Current call sites (grep `useNoIndex` to refresh this list):
 *   - AppLayout            covers all /app/* routes (Dashboard, Settings,
 *                          plus any future authenticated page mounted inside
 *                          this layout).
 *   - OnboardingLayout     /app/onboarding (top-level route outside AppLayout).
 *   - ImprintPage          /imprint
 *   - LegalPage            /legal (privacy)
 *   - LoginPage            /login (also /register since it redirects here)
 *   - NotFoundPage         /* (anything that 404s)
 *
 * Indexed routes (intentionally do NOT call this hook):
 *   - LandingPage          /
 *   - PricingPage          /pricing
 *
 * Adding a new noindex page: import this hook and call useNoIndex() inside
 * the component (or its parent layout). No central registry to update.
 *
 * Caveat: this is a client-side signal. Bots that don't execute JS won't see
 * the meta tag. For robust server-side enforcement, nginx can also serve a
 * matching X-Robots-Tag header on those paths. Recommended one-time block,
 * extensible by adding paths to the alternation list:
 *
 *   location ~ ^/(imprint|legal|login|register|app)(/|$) {
 *     add_header X-Robots-Tag "noindex, nofollow" always;
 *     try_files $uri /index.html;
 *   }
 *
 * Both layers together: any bot that fetches the URL gets noindex from the
 * header; any bot that runs JS also sees the meta tag. They're redundant by
 * design; either alone is sufficient for compliant crawlers.
 *
 * Do NOT also disallow these paths in robots.txt: a Disallow entry blocks
 * the crawler from fetching the page, so it never sees the noindex hint and
 * may still index the URL based on inbound links. Allow crawl, deny index.
 */
export function useNoIndex(): void {
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);
}
